import { dialogSelector, showDialog } from "@/redux/message/messageSlice";
import type { AppDispatch, IRootState } from "@/redux/store";
import { decideCreditsIntro } from "@repo/shared/credits";
import { creditsIntroSeen, markCreditsIntroSeen } from "@repo/shared/credits";
import {
  bucketsStaleSelector,
  creditsBalanceLoadedSelector,
  hasUnlimitedCreditsSelector,
  signupBonusRemainingSelector,
} from "@repo/shared/redux";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Introduces credits at the one moment they start to matter.
 *
 * A new user spends a sign-up bonus without ever being told credits exist (see
 * creditsAreSilentSelector). When that bonus runs out the credit UI appears, so
 * this is where it gets explained — once. The rules live in decideCreditsIntro;
 * this only supplies the state and applies the result.
 *
 * Mounted once, in RootLayout.
 */
export const useCreditsIntro = () => {
  const dispatch = useDispatch<AppDispatch>();
  const uuid = useSelector((state: IRootState) => state.userReducer.user?.uuid);
  const signupBonusRemaining = useSelector(signupBonusRemainingSelector);
  const balanceLoaded = useSelector(creditsBalanceLoadedSelector);
  const unlimited = useSelector(hasUnlimitedCreditsSelector);
  const bucketsStale = useSelector(bucketsStaleSelector);
  const activeModal = useSelector(dialogSelector);

  // Tracks the uuid alongside the latch, so a logout/login doesn't hand the
  // next user the previous user's transition — resetCredits zeroes the buckets,
  // which would otherwise read exactly like a bonus running out.
  const observed = useRef<{ uuid?: string; hadBonus: boolean }>({
    hadBonus: false,
  });

  useEffect(() => {
    if (observed.current.uuid !== uuid) {
      observed.current = { uuid, hadBonus: false };
    }

    const decision = decideCreditsIntro({
      uuid,
      signupBonusRemaining,
      balanceLoaded,
      unlimited,
      bucketsStale,
      openModalType: activeModal?.modalType,
      hadBonus: observed.current.hadBonus,
      alreadySeen: !!uuid && creditsIntroSeen(uuid),
    });

    observed.current.hadBonus = decision.hadBonus;
    if (decision.markSeen && uuid) {
      markCreditsIntroSeen(uuid);
    }
    if (decision.show) dispatch(showDialog({ modalType: "credits-intro" }));
  }, [
    uuid,
    signupBonusRemaining,
    balanceLoaded,
    unlimited,
    bucketsStale,
    activeModal,
    dispatch,
  ]);
};
